import { useMemo } from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import type { BadgeProps } from '@monksflow/monks-ui';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { useOverflowDetection } from './hooks';
import type {
  VersionChipColor,
  VersionSelectorProps,
  VersionMenuItemProps,
} from './VersionSelector.types';

/**
 * Tessra `Badge` carries intent, not hue, so the inherited palette names collapse onto the three
 * status variants plus a neutral default.
 */
const CHIP_VARIANT: Record<VersionChipColor, BadgeProps['variant']> = {
  red: 'destructive',
  orange: 'warning',
  yellow: 'warning',
  green: 'positive',
  blue: 'default',
  lightBlue: 'default',
  pink: 'default',
  grey: 'secondary',
};

const VersionMenuItem = ({
  version,
  versionText,
  relativeTime,
  isSelected,
  tagLabel,
  onVersionClick,
}: VersionMenuItemProps) => {
  const [textRef, isOverflowing] = useOverflowDetection<HTMLSpanElement>(versionText);

  return (
    <DropdownMenuItem
      onSelect={() => onVersionClick(version)}
      className={`relative gap-3 rounded-lg p-2${isSelected ? ' bg-accent' : ''}`}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex min-w-0 flex-1 flex-col pe-4">
            <span ref={textRef} className="truncate text-sm leading-5 text-foreground">
              {versionText}
            </span>

            <span className="text-sm leading-5 text-muted-foreground">
              {tagLabel} • {relativeTime}
            </span>
          </div>
        </TooltipTrigger>
        {isOverflowing && <TooltipContent side="top">{versionText}</TooltipContent>}
      </Tooltip>

      {isSelected && <CheckIcon className="absolute right-2 size-5 text-foreground" />}
    </DropdownMenuItem>
  );
};

/**
 * Dropdown selector for widget versions.
 */
export const VersionSelector = ({
  currentVersion,
  versions,
  onVersionSelect,
  chip,
  disabled = false,
  sortBy = 'timestamp',
}: VersionSelectorProps) => {
  const sortedVersions = useMemo(() => {
    return [...versions].sort((a, b) => {
      const aValue = Object.prototype.hasOwnProperty.call(a, sortBy) ? a[sortBy] : a.timestamp;
      const bValue = Object.prototype.hasOwnProperty.call(b, sortBy) ? b[sortBy] : b.timestamp;

      return bValue - aValue;
    });
  }, [sortBy, versions]);

  const formatTime = (timestamp: number) => {
    try {
      return formatRelativeDate(new Date(timestamp));
    } catch {
      return '';
    }
  };

  const hasChoices = versions.length > 1;

  return (
    <TooltipProvider>
      {chip && (
        <Badge variant={chip.color ? CHIP_VARIANT[chip.color] : 'secondary'}>{chip.label}</Badge>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" disabled={disabled || !hasChoices}>
            <span className="text-base leading-6 font-medium">{currentVersion.title}</span>
            {hasChoices && <ChevronDownIcon className="size-4 text-muted-foreground" />}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="flex w-80 flex-col gap-1 rounded-lg p-2">
          {sortedVersions.map((version) => (
            <VersionMenuItem
              key={version.id}
              version={version}
              versionText={version.title}
              relativeTime={formatTime(version.timestamp)}
              isSelected={version.id === currentVersion.id}
              tagLabel={version.agentLabel || chip?.label}
              onVersionClick={onVersionSelect}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
};
